import React, { useState, useEffect } from 'react';
import { Wallet } from '../../utils/wallet';
import { useStandings } from '../../hooks/useStandings';
import { API_BASE_URL } from '../../config';
import { getTeamAveragePoints, selectPlayers } from '../../utils/bettingUtils';

interface NextGame {
  team1: {
    id: string;
    name: string;
    color: string;
  };
  team2: {
    id: string;
    name: string;
    color: string;
  };
}

interface BettingPanelProps {
  nextGames: (NextGame | null)[];
  onCreditsUpdate?: (credits: number) => void;
}

interface PlayerStats {
  id: string;
  name: string;
  teamId: string;
  avgPoints: number;
  avgAssists: number;
  gamesPlayed: number;
}

interface RostersData {
  [teamId: string]: PlayerStats[];
}

const BettingPanel: React.FC<BettingPanelProps> = ({ nextGames, onCreditsUpdate }) => {
  const [bets, setBets] = useState(Wallet.getAllBets());
  const [isExpanded, setIsExpanded] = useState(false);
  const [rosters, setRosters] = useState<RostersData>({});
  const betAmount = 25; // Fixed bet amount for outcome
  const overUnderBetAmount = 100; // Fixed bet amount for over/under
  const { getTeamStanding } = useStandings();

  // Fetch rosters data
  useEffect(() => {
    const fetchRosters = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/rosters`);
        const data = await response.json();
        if (data.success && data.rosters) {
          setRosters(data.rosters);
        }
      } catch (error) {
        console.error('Error fetching rosters:', error);
      }
    };
    fetchRosters();
  }, []);

  // Update bets when nextGames change or on mount
  useEffect(() => {
    const newCredits = Wallet.getCredits();
    const newBets = Wallet.getAllBets();
    setBets(newBets);
    if (onCreditsUpdate) {
      onCreditsUpdate(newCredits);
    }
  }, [nextGames, onCreditsUpdate]);

  // Also update on interval to catch any missed updates
  useEffect(() => {
    const interval = setInterval(() => {
      const newBets = Wallet.getAllBets();
      setBets(newBets);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const toggleExpanded = () => {
    setIsExpanded(prev => !prev);
  };

  const handleBet = (gameId: string, teamId: string, teamName: string) => {
    try {
      const result = Wallet.placeBet(gameId, teamId, betAmount);
      setBets(Wallet.getAllBets());
      
      if (onCreditsUpdate) {
        onCreditsUpdate(result.totalCredits);
      }
      
      console.log(`Bet placed: ${betAmount} credits on ${teamName}`);
    } catch (error: any) {
      alert(error.message);
    }
  };

  const handleOverUnderBet = (gameId: string, betType: string, selection: 'over' | 'under') => {
    try {
      const bets = Wallet.getBetsForGame(gameId);
      const overKey = `${betType}-over`;
      const underKey = `${betType}-under`;
      
      // Check if opposite bet exists and clear it (radio button behavior)
      if (selection === 'over' && bets[underKey]) {
        // Refund the under bet
        Wallet.addCredits(bets[underKey]);
        const allBets = Wallet.getAllBets();
        if (allBets[gameId]) {
          delete allBets[gameId][underKey];
          Wallet.setBets(allBets);
        }
      } else if (selection === 'under' && bets[overKey]) {
        // Refund the over bet
        Wallet.addCredits(bets[overKey]);
        const allBets = Wallet.getAllBets();
        if (allBets[gameId]) {
          delete allBets[gameId][overKey];
          Wallet.setBets(allBets);
        }
      }
      
      // If same selection is clicked again, toggle it off (refund)
      const betKey = `${betType}-${selection}`;
      if (bets[betKey] && bets[betKey] > 0) {
        // Refund and clear
        Wallet.addCredits(bets[betKey]);
        const allBets = Wallet.getAllBets();
        if (allBets[gameId]) {
          delete allBets[gameId][betKey];
          Wallet.setBets(allBets);
        }
        setBets(Wallet.getAllBets());
        if (onCreditsUpdate) {
          onCreditsUpdate(Wallet.getCredits());
        }
        return;
      }
      
      // Place new bet
      const result = Wallet.placeBet(gameId, betKey, overUnderBetAmount);
      setBets(Wallet.getAllBets());
      
      if (onCreditsUpdate) {
        onCreditsUpdate(result.totalCredits);
      }
      
      console.log(`Bet placed: ${overUnderBetAmount} credits on ${betType} ${selection}`);
    } catch (error: any) {
      alert(error.message);
    }
  };

  const getBetAmountForTeam = (gameId: string, teamId: string) => {
    return bets[gameId]?.[teamId] || 0;
  };

  const getOverUnderBet = (gameId: string, betType: string, selection: 'over' | 'under') => {
    const betKey = `${betType}-${selection}`;
    return bets[gameId]?.[betKey] || 0;
  };

  const getBetIndicator = (gameId: string, betType: string): string => {
    const overBet = getOverUnderBet(gameId, betType, 'over');
    const underBet = getOverUnderBet(gameId, betType, 'under');
    
    if (overBet > 0) {
      return `(▲ ${overBet})`;
    } else if (underBet > 0) {
      return `(▼ ${underBet})`;
    }
    return '';
  };

  const getGameId = (game: NextGame, slot: number) => {
    return `next-${slot}-${game.team1.id}-${game.team2.id}`;
  };

  // Calculate odds based on team records
  const calculateOdds = (team1Standing: any, team2Standing: any): { team1: number; team2: number } => {
    if (!team1Standing || !team2Standing) {
      return { team1: -100, team2: +100 };
    }

    const team1WinPct = team1Standing.wins / (team1Standing.wins + team1Standing.losses || 1);
    const team2WinPct = team2Standing.wins / (team2Standing.wins + team2Standing.losses || 1);
    
    const winPctDiff = team1WinPct - team2WinPct;
    
    // Convert win percentage difference to odds
    // Positive diff = team1 favored (negative odds)
    // Negative diff = team2 favored (positive odds for team1)
    const baseOdds = winPctDiff * 200; // Scale to reasonable odds range
    
    const team1Odds = Math.round(baseOdds);
    const team2Odds = Math.round(-baseOdds);
    
    return {
      team1: team1Odds,
      team2: team2Odds
    };
  };


  return (
    <div style={{ width: '100%', paddingTop: '2rem' }}>
      {/* Next Games Betting - Side by Side */}
      <div className="betting-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4rem' }}>
        {nextGames.map((game, slot) => {
          if (!game) return null;
          
          const gameId = getGameId(game, slot);
          const team1Bet = getBetAmountForTeam(gameId, game.team1.id);
          const team2Bet = getBetAmountForTeam(gameId, game.team2.id);
          const team1Standing = getTeamStanding(game.team1.id);
          const team2Standing = getTeamStanding(game.team2.id);
          const team1Record = team1Standing ? `${team1Standing.wins}-${team1Standing.losses}` : '0-0';
          const team2Record = team2Standing ? `${team2Standing.wins}-${team2Standing.losses}` : '0-0';
          const odds = calculateOdds(team1Standing, team2Standing);
          const team1AvgPoints = getTeamAveragePoints(team1Standing);
          const team2AvgPoints = getTeamAveragePoints(team2Standing);
          
          // Get players for this game
          const team1Players = rosters[game.team1.id] || [];
          const team2Players = rosters[game.team2.id] || [];
          const selectedPlayers = selectPlayers(gameId, team1Players, team2Players);
          
          return (
            <div key={gameId} className="table--team">
              {/* Header */}
              <div className="thead">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>Game {slot + 1} OUTCOME</div>
                  {/* <div style={{ fontSize: '12px', color: '#ffffff8a', fontWeight: 'normal'}}>GAME {slot + 1}</div> */}
                </div>
              </div>
              
              {/* Team 1 Row */}
              <div className="trow" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'top', gap: '0.5rem', flex: '1' }}>
                  <div 
                    style={{ 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '50%', 
                      marginTop: '.6rem',
                      backgroundColor: game.team1.color || '#00ff88',
                      flexShrink: 0
                    }}
                  ></div>
                  <div style={{ flex: '1' }}>
                    <div style={{ color: '#fff', fontSize: '13px' }}>{game.team1.name}</div>
                    <div style={{ color: '#ffffff6a', fontSize: '11px' }}>
                      {team1Record} / {odds.team1 > 0 ? '+' : ''}{odds.team1}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
                   <div style={{ color: '#fff', fontFamily: 'monospace', fontSize: '13px', width: '32px', textAlign: 'right' }}>
                    {team1Bet}
                  </div>
                  <button
                    onClick={() => handleBet(gameId, game.team1.id, game.team1.name)}
                    disabled={!Wallet.canAfford(betAmount)}
                    style={{
                      backgroundColor: '#1E1E1E',
                      color: '#fff',
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      padding: '0.25rem 0.75rem',
                      border: 'none',
                      borderRadius: '0px',
                      cursor: Wallet.canAfford(betAmount) ? 'pointer' : 'not-allowed',
                      opacity: Wallet.canAfford(betAmount) ? 1 : 0.5
                    }}
                  >
                    Bet {betAmount} Σ
                  </button>
                </div>
              </div>
              
              {/* Team 2 Row */}
              <div className="trow" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid #000' }}>
                <div style={{ display: 'flex', alignItems: 'top', gap: '0.5rem', flex: '1' }}>
                  <div 
                    style={{ 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '50%',
                      marginTop: '.6rem',
                      backgroundColor: game.team2.color || '#ffaa00',
                      flexShrink: 0
                    }}
                  ></div>
                  <div style={{ flex: '1' }}>
                    <div style={{ color: '#fff', fontSize: '13px' }}>{game.team2.name}</div>
                    <div style={{ color: '#ffffff6a', fontSize: '11px' }}>
                      {team2Record} / {odds.team2 > 0 ? '+' : ''}{odds.team2}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
                  <div style={{ color: '#fff', fontFamily: 'monospace', fontSize: '13px', width: '32px', textAlign: 'right' }}>
                    {team2Bet}
                  </div>
                  <button
                    onClick={() => handleBet(gameId, game.team2.id, game.team2.name)}
                    disabled={!Wallet.canAfford(betAmount)}
                    style={{
                      backgroundColor: '#1E1E1E',
                      color: '#fff',
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      padding: '0.25rem 0.75rem',
                      border: 'none',
                      borderRadius: '0px',
                      cursor: Wallet.canAfford(betAmount) ? 'pointer' : 'not-allowed',
                      opacity: Wallet.canAfford(betAmount) ? 1 : 0.5
                    }}
                  >
                    Bet {betAmount} Σ
                  </button>
                 
                </div>
              </div>

              {/* Expandable Section */}
              {isExpanded && (
                <>
                  {/* Total PTS Over/Under */}
                  <div style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
                    <div style={{ color: '#fff', fontSize: '12px', textTransform: 'uppercase', borderBottom: '1px solid #fff'}}>
                      TOTAL PTS OVER/UNDER
                    </div>
                    
                    {/* Team 1 Points */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 0 0.35rem', borderBottom: '1px solid #404040' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1' }}>
                        <div 
                          style={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: game.team1.color || '#00ff88',
                            flexShrink: 0
                          }}
                        ></div>
                        <div style={{ color: '#fff', fontSize: '11px', fontFamily: 'monospace' }}>
                          {game.team1.name.substring(0, 2).toUpperCase()}
                        </div>

                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <button
                          onClick={() => handleOverUnderBet(gameId, `team1-pts`, 'under')}
                          disabled={!Wallet.canAfford(overUnderBetAmount)}
                          style={{
                            backgroundColor: '#1E1E1E',
                            color: '#fff',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            padding: '0.25rem 0.5rem',
                            border: getOverUnderBet(gameId, `team1-pts`, 'under') > 0 ? '1px solid #fff' : '1px solid #1E1E1E',
                            borderRadius: '0',
                            cursor: Wallet.canAfford(overUnderBetAmount) ? 'pointer' : 'not-allowed',
                            opacity: Wallet.canAfford(overUnderBetAmount) ? 1 : 0.5
                          }}
                        >
                          <span style={{ color: '#C91111' }}>▼</span> {overUnderBetAmount} Σ
                        </button>

                        <div style={{ color: '#ffffff', fontSize: '12px', fontFamily: 'monospace', minWidth: '52px' ,textAlign: 'center' }}>
                          {team1AvgPoints} pts
                        </div>

                        <button
                          onClick={() => handleOverUnderBet(gameId, `team1-pts`, 'over')}
                          disabled={!Wallet.canAfford(overUnderBetAmount)}
                          style={{
                            backgroundColor: '#1E1E1E',
                            color: '#fff',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            padding: '0.25rem 0.5rem',
                            border: getOverUnderBet(gameId, `team1-pts`, 'over') > 0 ? '1px solid #fff' : '1px solid #1E1E1E',
                            borderRadius: '0px',
                            cursor: Wallet.canAfford(overUnderBetAmount) ? 'pointer' : 'not-allowed',
                            opacity: Wallet.canAfford(overUnderBetAmount) ? 1 : 0.5
                          }}
                        >
                           <span style={{ color: '#128700' }}>▲</span> {overUnderBetAmount} Σ
                        </button>
                      </div>
                    </div>

                    {/* Team 2 Points */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 0 0.35rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1' }}>
                        <div 
                          style={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: game.team2.color || '#ffaa00',
                            flexShrink: 0
                          }}
                        ></div>
                        <div style={{ color: '#fff', fontSize: '11px', fontFamily: 'monospace' }}>
                          {game.team2.name.substring(0, 2).toUpperCase()}
                        </div>
                        
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <button
                          onClick={() => handleOverUnderBet(gameId, `team2-pts`, 'under')}
                          disabled={!Wallet.canAfford(overUnderBetAmount)}
                          style={{
                            backgroundColor: '#1E1E1E',
                            color: '#fff',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            padding: '0.25rem 0.5rem',
                            border: getOverUnderBet(gameId, `team2-pts`, 'under') > 0 ? '1px solid #fff' : '1px solid #1E1E1E',
                            borderRadius: '0px',
                            cursor: Wallet.canAfford(overUnderBetAmount) ? 'pointer' : 'not-allowed',
                            opacity: Wallet.canAfford(overUnderBetAmount) ? 1 : 0.5
                          }}
                        >
                          <span style={{ color: '#C91111' }}>▼</span> {overUnderBetAmount} Σ
                        </button>

                        <div style={{ color: '#ffffff', fontSize: '12px', fontFamily: 'monospace' }}>
                          {team2AvgPoints} pts
                        </div>

                        <button
                          onClick={() => handleOverUnderBet(gameId, `team2-pts`, 'over')}
                          disabled={!Wallet.canAfford(overUnderBetAmount)}
                          style={{
                            backgroundColor: '#1E1E1E',
                            color: '#fff',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            padding: '0.25rem 0.5rem',
                            border: getOverUnderBet(gameId, `team2-pts`, 'over') > 0 ? '1px solid #fff' : '1px solid #1E1E1E',
                            borderRadius: '0px',
                            cursor: Wallet.canAfford(overUnderBetAmount) ? 'pointer' : 'not-allowed',
                            opacity: Wallet.canAfford(overUnderBetAmount) ? 1 : 0.5
                          }}
                        >
                          <span style={{ color: '#128700' }}>▲</span> {overUnderBetAmount} Σ
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Player Over/Under */}
                  <div style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
                    <div style={{ color: '#fff', fontSize: '12px', textTransform: 'uppercase', borderBottom: '1px solid #fff'}}>
                      PLAYER OVER/UNDER
                    </div>
                    
                    {/* Team 1 Points Player */}
                    {selectedPlayers.team1Points && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 0 0.35rem', borderBottom: '1px solid #404040' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1' }}>
                          <div 
                            style={{ 
                              width: '8px', 
                              height: '8px', 
                              borderRadius: '50%', 
                              backgroundColor: game.team1.color || '#00ff88',
                              flexShrink: 0
                            }}
                          ></div>
                          <div style={{ color: '#fff', fontSize: '11px' }}>{selectedPlayers.team1Points.name}</div>
                          
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <button
                            onClick={() => handleOverUnderBet(gameId, `player-${selectedPlayers.team1Points!.id}-pts`, 'under')}
                            disabled={!Wallet.canAfford(overUnderBetAmount)}
                            style={{
                              backgroundColor: '#1E1E1E',
                              color: '#fff',
                              fontFamily: 'monospace',
                              fontSize: '11px',
                              padding: '0.25rem 0.5rem',
                              border: getOverUnderBet(gameId, `player-${selectedPlayers.team1Points!.id}-pts`, 'under') > 0 ? '1px solid #fff' : '1px solid #1E1E1E',
                              borderRadius: '0px',
                              cursor: Wallet.canAfford(overUnderBetAmount) ? 'pointer' : 'not-allowed',
                              opacity: Wallet.canAfford(overUnderBetAmount) ? 1 : 0.5
                            }}
                          >
                            <span style={{ color: '#C91111' }}>▼</span> {overUnderBetAmount} Σ
                          </button>

                          <div style={{ color: '#ffffff', fontSize: '12px', fontFamily: 'monospace', minWidth: '52px' ,textAlign: 'center' }}>
                            {Math.round(selectedPlayers.team1Points.avgPoints)} pts
                          </div>

                          <button
                            onClick={() => handleOverUnderBet(gameId, `player-${selectedPlayers.team1Points!.id}-pts`, 'over')}
                            disabled={!Wallet.canAfford(overUnderBetAmount)}
                            style={{
                              backgroundColor: '#1E1E1E',
                              color: '#fff',
                              fontFamily: 'monospace',
                              fontSize: '11px',
                              padding: '0.25rem 0.5rem',
                              border: getOverUnderBet(gameId, `player-${selectedPlayers.team1Points!.id}-pts`, 'over') > 0 ? '1px solid #fff' : '1px solid #1E1E1E',
                              borderRadius: '0px',
                              cursor: Wallet.canAfford(overUnderBetAmount) ? 'pointer' : 'not-allowed',
                              opacity: Wallet.canAfford(overUnderBetAmount) ? 1 : 0.5
                            }}
                          >
                            <span style={{ color: '#128700' }}>▲</span> {overUnderBetAmount} Σ
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Team 1 Assists Player */}
                    {selectedPlayers.team1Assists && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 0 0.35rem', borderBottom: '1px solid #404040' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1' }}>
                          <div 
                            style={{ 
                              width: '8px', 
                              height: '8px', 
                              borderRadius: '50%', 
                              backgroundColor: game.team1.color || '#00ff88',
                              flexShrink: 0
                            }}
                          ></div>
                          <div style={{ color: '#fff', fontSize: '11px' }}>{selectedPlayers.team1Assists.name}</div>
                          
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <button
                            onClick={() => handleOverUnderBet(gameId, `player-${selectedPlayers.team1Assists!.id}-ast`, 'under')}
                            disabled={!Wallet.canAfford(overUnderBetAmount)}
                            style={{
                              backgroundColor: '#1E1E1E',
                              color: '#fff',
                              fontFamily: 'monospace',
                              fontSize: '11px',
                              padding: '0.25rem 0.5rem',
                              border: getOverUnderBet(gameId, `player-${selectedPlayers.team1Assists!.id}-ast`, 'under') > 0 ? '1px solid #fff' : '1px solid #1E1E1E',
                              borderRadius: '0px',
                              cursor: Wallet.canAfford(overUnderBetAmount) ? 'pointer' : 'not-allowed',
                              opacity: Wallet.canAfford(overUnderBetAmount) ? 1 : 0.5
                            }}
                          >
                            <span style={{ color: '#C91111' }}>▼</span> {overUnderBetAmount} Σ
                          </button>

                          <div style={{ color: '#ffffff', fontSize: '12px', fontFamily: 'monospace', minWidth: '52px' ,textAlign: 'center' }}>
                            {Math.round(selectedPlayers.team1Assists.avgAssists)} ast
                          </div>
                          <button
                            onClick={() => handleOverUnderBet(gameId, `player-${selectedPlayers.team1Assists!.id}-ast`, 'over')}
                            disabled={!Wallet.canAfford(overUnderBetAmount)}
                            style={{
                              backgroundColor: '#1E1E1E',
                              color: '#fff',
                              fontFamily: 'monospace',
                              fontSize: '11px',
                              padding: '0.25rem 0.5rem',
                              border: getOverUnderBet(gameId, `player-${selectedPlayers.team1Assists!.id}-ast`, 'over') > 0 ? '1px solid #fff' : '1px solid #1E1E1E',
                              borderRadius: '0px',
                              cursor: Wallet.canAfford(overUnderBetAmount) ? 'pointer' : 'not-allowed',
                              opacity: Wallet.canAfford(overUnderBetAmount) ? 1 : 0.5
                            }}
                          >
                            <span style={{ color: '#128700' }}>▲</span> {overUnderBetAmount} Σ
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Team 2 Points Player */}
                    {selectedPlayers.team2Points && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 0 0.35rem', borderBottom: '1px solid #404040' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1' }}>
                          <div 
                            style={{ 
                              width: '8px', 
                              height: '8px', 
                              borderRadius: '50%', 
                              backgroundColor: game.team2.color || '#ffaa00',
                              flexShrink: 0
                            }}
                          ></div>
                          <div style={{ color: '#fff', fontSize: '11px' }}>{selectedPlayers.team2Points.name}</div>
                          
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <button
                            onClick={() => handleOverUnderBet(gameId, `player-${selectedPlayers.team2Points!.id}-pts`, 'under')}
                            disabled={!Wallet.canAfford(overUnderBetAmount)}
                            style={{
                              backgroundColor: '#1E1E1E',
                              color: '#fff',
                              fontFamily: 'monospace',
                              fontSize: '11px',
                              padding: '0.25rem 0.5rem',
                              border: getOverUnderBet(gameId, `player-${selectedPlayers.team2Points!.id}-pts`, 'under') > 0 ? '1px solid #fff' : '1px solid #1E1E1E',
                              borderRadius: '0px',
                              cursor: Wallet.canAfford(overUnderBetAmount) ? 'pointer' : 'not-allowed',
                              opacity: Wallet.canAfford(overUnderBetAmount) ? 1 : 0.5
                            }}
                          >
                            <span style={{ color: '#C91111' }}>▼</span> {overUnderBetAmount} Σ
                          </button>

                          <div style={{ color: '#ffffff', fontSize: '12px', fontFamily: 'monospace', minWidth: '52px' ,textAlign: 'center' }}>
                            {Math.round(selectedPlayers.team2Points.avgPoints)} pts
                          </div>

                          <button
                            onClick={() => handleOverUnderBet(gameId, `player-${selectedPlayers.team2Points!.id}-pts`, 'over')}
                            disabled={!Wallet.canAfford(overUnderBetAmount)}
                            style={{
                              backgroundColor: '#1E1E1E',
                              color: '#fff',
                              fontFamily: 'monospace',
                              fontSize: '11px',
                              padding: '0.25rem 0.5rem',
                              border: getOverUnderBet(gameId, `player-${selectedPlayers.team2Points!.id}-pts`, 'over') > 0 ? '1px solid #fff' : '1px solid #1E1E1E',
                              borderRadius: '0px',
                              cursor: Wallet.canAfford(overUnderBetAmount) ? 'pointer' : 'not-allowed',
                              opacity: Wallet.canAfford(overUnderBetAmount) ? 1 : 0.5
                            }}
                          >
                            <span style={{ color: '#128700' }}>▲</span> {overUnderBetAmount} Σ
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Team 2 Assists Player */}
                    {selectedPlayers.team2Assists && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 0 0.35rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1' }}>
                          <div 
                            style={{ 
                              width: '8px', 
                              height: '8px', 
                              borderRadius: '50%', 
                              backgroundColor: game.team2.color || '#ffaa00',
                              flexShrink: 0
                            }}
                          ></div>
                          <div style={{ color: '#fff', fontSize: '11px' }}>{selectedPlayers.team2Assists.name}</div>
                          
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <button
                            onClick={() => handleOverUnderBet(gameId, `player-${selectedPlayers.team2Assists!.id}-ast`, 'under')}
                            disabled={!Wallet.canAfford(overUnderBetAmount)}
                            style={{
                              backgroundColor: '#1E1E1E',
                              color: '#fff',
                              fontFamily: 'monospace',
                              fontSize: '11px',
                              padding: '0.25rem 0.5rem',
                              border: getOverUnderBet(gameId, `player-${selectedPlayers.team2Assists!.id}-ast`, 'under') > 0 ? '1px solid #fff' : '1px solid #1E1E1E',
                              borderRadius: '0px',
                              cursor: Wallet.canAfford(overUnderBetAmount) ? 'pointer' : 'not-allowed',
                              opacity: Wallet.canAfford(overUnderBetAmount) ? 1 : 0.5
                            }}
                          >
                            <span style={{ color: '#C91111' }}>▼</span> {overUnderBetAmount} Σ
                          </button>

                          <div style={{ color: '#ffffff', fontSize: '12px', fontFamily: 'monospace', minWidth: '52px' ,textAlign: 'center'}}>
                            {Math.round(selectedPlayers.team2Assists.avgAssists)} ast
                          </div>

                          <button
                            onClick={() => handleOverUnderBet(gameId, `player-${selectedPlayers.team2Assists!.id}-ast`, 'over')}
                            disabled={!Wallet.canAfford(overUnderBetAmount)}
                            style={{
                              backgroundColor: '#1E1E1E',
                              color: '#fff',
                              fontFamily: 'monospace',
                              fontSize: '11px',
                              padding: '0.25rem 0.5rem',
                              border: getOverUnderBet(gameId, `player-${selectedPlayers.team2Assists!.id}-ast`, 'over') > 0 ? '1px solid #fff' : '1px solid #1E1E1E',
                              borderRadius: '0px',
                              cursor: Wallet.canAfford(overUnderBetAmount) ? 'pointer' : 'not-allowed',
                              opacity: Wallet.canAfford(overUnderBetAmount) ? 1 : 0.5
                            }}
                          >
                            <span style={{ color: '#128700' }}>▲</span> {overUnderBetAmount} Σ
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

            </div>
          );
        })}
        
        {nextGames.filter(g => g).length === 0 && (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem 0', color: '#ffffff6a', fontFamily: 'monospace' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>⏳</div>
            <div style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>Preparing Next Games...</div>
            <div style={{ fontSize: '0.875rem' }}>New betting opportunities coming soon</div>
          </div>
        )}
      </div>
      
      {/* Single Toggle Button for Both Games */}
      {nextGames.filter(g => g).length > 0 && (
        <div style={{ marginTop: '3rem', textAlign: 'center', marginLeft: '-24px', marginRight: '-24px' }}>
          <button
            onClick={toggleExpanded}
            style={{
              backgroundColor: 'transparent',
              color: '#404040',
              fontFamily: 'monospace',
              fontSize: '11px',
              padding: '0.5rem 1rem',
              borderTop: '1px solid #3B3B3B',
              borderRadius: '2px',
              cursor: 'pointer',
              textTransform: 'uppercase',
              display: 'block',
              width: '100%',
            }}
          >
            {isExpanded ? 'HIDE FULL BETTING OPTIONS' : 'SHOW FULL BETTING OPTIONS'}
          </button>
        </div>
      )}
      <div className="divider game"></div>
    </div>
  );
};

export default BettingPanel;
